import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';
import FileDropzone from './FileDropzone';
import { InputFileImage } from './InputFileImage';

const FormEditDownloadFile = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [fileData, setFileData] = useState({
        nome: '',
        img_preview: '',
        notificar: false,
        categoria: '',
        data_atualizacao: '',
        arq: '',
    });

    const [fileErrorData, setFileErrorData] = useState({
        nome: [],
        img_preview: [],
        notificar: [],
        categoria: [],
        data_atualizacao: '',
        arq: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFileData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setFileData((prevData) => ({
            ...prevData,
            [name]: selectedOption.value,
        }));
    };

    const handleImageCrop = (croppedImage, fileExtenstion, name) => {
        setFileData((prevData) => ({
            ...prevData,
            [name]: croppedImage,
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const [fileResponse, categoriesResponse] = await Promise.all([
                    fetch(`${baseUrl}/manager/downloads/arquivos/${id}`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                    fetch(`${baseUrl}/manager/downloads/categorias/`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                ]);

                if (fileResponse.ok) {
                    const fileData = await fileResponse.json();
                    setFileData(fileData.download || []);
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar o download.',
                        show: true,
                    });
                }

                if (categoriesResponse.ok) {
                    const categoriesData = await categoriesResponse.json();
                    setCategories(
                        categoriesData.categorias_downloads.map((categoria) => ({
                            value: categoria.id,
                            label: categoria.nome,
                        }))
                    );
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar as categorias.',
                        show: true,
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro na requisição',
                    show: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const formData = new FormData();

            Object.keys(fileData).forEach((key) => {
                if (key !== 'img_preview' && key !== 'arq') {
                    if (typeof fileData[key] === 'boolean') {
                        formData.append(key, fileData[key] ? 1 : 0);
                    } else {
                        formData.append(key, fileData[key]);
                    }
                }
            });

            if (fileData.img_preview) {
                formData.append('img_preview', fileData.img_preview);
            }
            if (fileData.arq) {
                formData.append('arq', fileData.arq);
            }

            const response = await fetch(`${baseUrl}/manager/downloads/arquivos/atualizar/${id}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Download atualizado com sucesso!',
                    show: true,
                });
                navigate(`/manager/downloads/arquivos/${fileData.categoria}`);
            } else {
                if (response.status === 422) {
                    const errors = await response.json();
                    setFileErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar arquivo.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent" />
            </div>
        );

    return (
        <>
            <Link
                to={`/manager/downloads/arquivos/${fileData.categoria}`}
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Editar download</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={fileData.nome}
                                    onClick={() => setFileErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {fileErrorData.nome && fileErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {fileErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setFileErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-2/5">
                                <label className="block text-sm font-medium mb-1">Categoria</label>
                                <Select
                                    name="categoria"
                                    options={categories}
                                    value={categories.find((option) => option.value === fileData.categoria) || null}
                                    onClick={() => setFileErrorData((prevData) => ({ ...prevData, categoria: [] }))}
                                    onChange={(selectedOption) => handleSelectChange('categoria', selectedOption)}
                                    placeholder="Selecione uma opção..."
                                    classNamePrefix="admin-select"
                                />
                                {fileErrorData.categoria && fileErrorData.categoria[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {fileErrorData.categoria[0]}{' '}
                                        <span
                                            onClick={() => setFileErrorData((prevData) => ({ ...prevData, categoria: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/5">
                                <label className="block text-sm font-medium mb-1">Data Atualização</label>
                                <input
                                    type="date"
                                    name="data_atualizacao"
                                    value={fileData.data_atualizacao}
                                    onClick={() => setFileErrorData((prevData) => ({ ...prevData, data_atualizacao: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1 2xl:py-[7px] border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {fileErrorData.data_atualizacao && fileErrorData.data_atualizacao[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {fileErrorData.data_atualizacao[0]}{' '}
                                        <span
                                            onClick={() => setFileErrorData((prevData) => ({ ...prevData, data_atualizacao: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium mb-1">Notificar usuários?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="notificar"
                                        checked={fileData.notificar}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {fileErrorData.notificar && fileErrorData.notificar[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {fileErrorData.notificar[0]}{' '}
                                        <span
                                            onClick={() => setFileErrorData((prevData) => ({ ...prevData, notificar: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-5/12">
                                <label className="block text-sm font-medium mb-1">Imagem prévia</label>

                                <InputFileImage
                                    name="img_preview"
                                    imagem={fileData.preview ?? '/assets/img/admin/image-select.png'}
                                    size={{ largura: '300', altura: '300' }}
                                    allowCrop={true}
                                    onImageCrop={handleImageCrop}
                                />
                            </div>

                            <div className="w-1/3">
                                <label className="block text-sm font-medium mb-1">Material</label>

                                {fileData.arquivo && (
                                    <a
                                        href={fileData.arquivo}
                                        className="block mt-2 mb-4 text-sm underline"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Visualizar arquivo atual
                                    </a>
                                )}
                                <FileDropzone
                                    type="all"
                                    name="arq"
                                    value={fileData.arq}
                                    onChange={(file) => {
                                        setFileData((prev) => ({ ...prev, arq: file }));
                                        setFileErrorData((prevData) => ({ ...prevData, arq: [] }));
                                    }}
                                    onDelete={() => setFileData((prev) => ({ ...prev, arq: null }))}
                                />

                                {fileErrorData.arq && fileErrorData.arq[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {fileErrorData.arq[0]}{' '}
                                        <span
                                            onClick={() => setFileErrorData((prevData) => ({ ...prevData, arq: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditDownloadFile;
